import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowLeft } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function loginWithGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created!");
      navigate("/book");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="signup-page" className="min-h-screen bg-pink-50 grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#4B6043] text-white relative overflow-hidden">
        <div className="blob bg-pink-500 w-[400px] h-[400px] -top-24 -right-20 opacity-40" />
        <Link to="/" className="relative z-10 flex items-center gap-2 font-heading font-black text-2xl">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-2xl bg-pink-500"><Sparkles className="w-5 h-5" /></span>
          Foldora
        </Link>
        <div className="relative z-10">
          <h2 className="font-heading text-4xl font-black tracking-tighter leading-tight">
            Skip the laundromat.<br />Book pickups in <span className="text-pink-400">seconds</span>.
          </h2>
          <p className="mt-4 text-stone-300 max-w-sm">Create a free account and get your first pickup scheduled today.</p>
        </div>
        <div className="relative z-10 text-sm text-stone-400">© {new Date().getFullYear()} Foldora</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md p-8 rounded-3xl border border-stone-200 bg-white shadow-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-stone-500 hover:text-pink-600 text-sm font-semibold mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-stone-900">Create your account</h1>
          <p className="mt-1 text-stone-500">Get 10% off your first order.</p>

          <Button
            data-testid="google-signup-btn"
            onClick={loginWithGoogle}
            variant="outline"
            className="mt-6 w-full h-12 rounded-full border-stone-300 font-bold"
          >
            <img alt="google" src="https://www.svgrepo.com/show/475656/google-color.svg" className="w-5 h-5 mr-2" />
            Sign up with Google
          </Button>

          <div className="flex items-center gap-4 my-6 text-xs text-stone-400 uppercase tracking-widest">
            <div className="flex-1 h-px bg-stone-200" /> or <div className="flex-1 h-px bg-stone-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input data-testid="signup-name-input" id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-1 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input data-testid="signup-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input data-testid="signup-password-input" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 h-12 rounded-xl" />
            </div>
            <Button data-testid="signup-submit-btn" disabled={loading} className="w-full h-12 rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-stone-600">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-login-link" className="font-bold text-pink-600 hover:underline">Log in</Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
